// frontend/src/hooks/useSwipeNavigation.js
// Horizontal swipe navigation between main tabs — iOS native & mobile web.
// All four swipeable panels stay mounted; this hook animates between them
// by sliding individual panels so there is no blank intermediate state.

import { useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

// Only the primary nav tabs participate in swipe navigation.
// Genotype and Settings live in the hamburger menu and are excluded intentionally.
const SWIPEABLE_VIEWS = ['dashboard', 'mice', 'cages', 'litters'];

// Minimum horizontal distance (px) required to trigger a view change.
const MIN_SWIPE_DISTANCE = 50;

// Duration (ms) of the slide transition.
const TRANSITION_MS = 250;

// Helper: apply a batch of inline styles to an element.
function applyStyles(el, styles) {
    Object.assign(el.style, styles);
}

// Helper: clear all animation-related inline styles from a panel.
function clearStyles(el) {
    ['position', 'top', 'left', 'right', 'bottom',
     'transform', 'transition', 'display', 'overflowY',
    ].forEach(prop => { el.style[prop] = ''; });
}

export function useSwipeNavigation(activeView, setActiveView) {
    const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
    const isMobileWeb = !Capacitor.isNativePlatform() && window.innerWidth <= 768;

    const touchStart = useRef(null);
    // null = direction undecided, true = horizontal, false = vertical
    const isHorizontalSwipe = useRef(null);
    const transitioning = useRef(false);

    const navigate = useCallback((direction) => {
        const currentIndex = SWIPEABLE_VIEWS.indexOf(activeView);
        if (currentIndex === -1 || transitioning.current) return;

        let nextIndex;
        if (direction === 'left' && currentIndex < SWIPEABLE_VIEWS.length - 1) {
            nextIndex = currentIndex + 1;
        } else if (direction === 'right' && currentIndex > 0) {
            nextIndex = currentIndex - 1;
        } else {
            return;
        }

        const wrapper = document.querySelector('.swipe-content-wrapper');
        const outPanel = wrapper?.querySelector(`[data-swipe-view="${activeView}"]`);
        const inPanel = wrapper?.querySelector(`[data-swipe-view="${SWIPEABLE_VIEWS[nextIndex]}"]`);
        if (!outPanel || !inPanel) return;

        transitioning.current = true;

        // Add class that hides FABs via CSS — setting `transform` on a parent
        // creates a new containing block that breaks position:fixed.
        wrapper.classList.add('swipe-transitioning');

        // Lock the wrapper so animated panels don't cause scrollbars.
        wrapper.style.overflow = 'hidden';
        wrapper.style.position = 'relative';

        const absPanel = {
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            overflowY: 'hidden',
        };

        // Position outgoing panel absolutely (it's currently visible via .swipe-panel-active).
        applyStyles(outPanel, absPanel);

        // Show incoming panel off-screen on the appropriate side.
        applyStyles(inPanel, {
            ...absPanel,
            display: 'block',
            transform: `translateX(${direction === 'left' ? '100%' : '-100%'})`,
        });

        // Force reflow so the browser registers starting positions.
        void inPanel.offsetWidth;

        // Animate both panels simultaneously.
        const transition = `transform ${TRANSITION_MS}ms ease-out`;
        outPanel.style.transition = transition;
        inPanel.style.transition = transition;

        outPanel.style.transform = `translateX(${direction === 'left' ? '-100%' : '100%'})`;
        inPanel.style.transform = 'translateX(0)';

        setTimeout(() => {
            // Clean up all inline styles.
            clearStyles(outPanel);
            clearStyles(inPanel);
            wrapper.style.overflow = '';
            wrapper.style.position = '';

            // Let React take over visibility via the swipe-panel-active class.
            setActiveView(SWIPEABLE_VIEWS[nextIndex]);

            // Restore FABs after React has re-rendered with the new active panel.
            requestAnimationFrame(() => {
                wrapper.classList.remove('swipe-transitioning');
                transitioning.current = false;
            });
        }, TRANSITION_MS);
    }, [activeView, setActiveView]);

    useEffect(() => {
        if (!isIOS && !isMobileWeb) return;

        // Check if an element or any ancestor is horizontally scrollable
        const isInsideHorizontalScroller = (el) => {
            while (el && el !== document.body) {
                if (el.scrollWidth > el.clientWidth + 1) {
                    const style = window.getComputedStyle(el);
                    const overflowX = style.overflowX;
                    if (overflowX === 'auto' || overflowX === 'scroll') return true;
                }
                el = el.parentElement;
            }
            return false;
        };

        const handleTouchStart = (e) => {
            if (transitioning.current) return;
            if (isInsideHorizontalScroller(e.target)) {
                touchStart.current = null;
                return;
            }
            touchStart.current = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
            };
            isHorizontalSwipe.current = null;
        };

        const handleTouchMove = (e) => {
            if (!touchStart.current || isHorizontalSwipe.current !== null) return;
            const dx = Math.abs(e.touches[0].clientX - touchStart.current.x);
            const dy = Math.abs(e.touches[0].clientY - touchStart.current.y);
            if (dx > 5 || dy > 5) {
                isHorizontalSwipe.current = dx > dy;
            }
        };

        const handleTouchEnd = (e) => {
            if (!touchStart.current || !isHorizontalSwipe.current) {
                touchStart.current = null;
                return;
            }

            const dx = e.changedTouches[0].clientX - touchStart.current.x;
            const dy = e.changedTouches[0].clientY - touchStart.current.y;
            touchStart.current = null;

            if (Math.abs(dx) < MIN_SWIPE_DISTANCE || Math.abs(dx) < Math.abs(dy) * 1.5) return;

            if (dx < 0) {
                navigate('left');
            } else {
                navigate('right');
            }
        };

        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: true });
        document.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isIOS, isMobileWeb, navigate]);
}
